import { useEffect, useMemo, useState } from 'react';
import type { ResourceAllocation } from '@/types';
import type { RoleMonthlyHoursByProject } from '@/types/assignment.types';
import { fetchRoleMonthlyHoursForProjects } from '@/lib/data-service';

interface UseRoleMonthlyHoursOptions {
  /** Allocations the availability calculation will consider (all people, all projects) */
  allocations: ResourceAllocation[];
  /** Start of the window availability is calculated over */
  windowStart: string;
  /** End of the window availability is calculated over */
  windowEnd: string;
  /** Project being assigned to — its own allocations are not conflicts */
  currentProjectId?: number;
  /** Skip fetching while the assignment UI is closed */
  enabled?: boolean;
}

/**
 * Month-by-month resource budget hours for the projects a person's existing
 * allocations belong to, so availability can count only the hours that fall
 * inside the target project's date window.
 */
export function useRoleMonthlyHours({
  allocations,
  windowStart,
  windowEnd,
  currentProjectId,
  enabled = true,
}: UseRoleMonthlyHoursOptions): RoleMonthlyHoursByProject {
  const [roleMonthlyHours, setRoleMonthlyHours] = useState<RoleMonthlyHoursByProject>(new Map());

  const conflictingProjects = useMemo(() => {
    if (!enabled) return [] as Array<{ projectId: number; startDate?: string }>;

    const start = windowStart ? new Date(windowStart) : null;
    const end = windowEnd ? new Date(windowEnd) : null;
    const byProject = new Map<number, string | undefined>();

    for (const alloc of allocations) {
      if (currentProjectId != null && alloc.projectId === currentProjectId) continue;
      if (start && end) {
        const allocStart = new Date(alloc.startDate);
        const allocEnd = new Date(alloc.endDate);
        if (allocStart > end || allocEnd < start) continue;
      }
      if (!byProject.has(alloc.projectId)) byProject.set(alloc.projectId, alloc.startDate);
    }

    return Array.from(byProject.entries()).map(([projectId, startDate]) => ({ projectId, startDate }));
  }, [allocations, windowStart, windowEnd, currentProjectId, enabled]);

  const projectsKey = conflictingProjects.map(p => p.projectId).sort((a, b) => a - b).join(',');

  useEffect(() => {
    if (conflictingProjects.length === 0) {
      setRoleMonthlyHours(new Map());
      return;
    }

    let cancelled = false;
    fetchRoleMonthlyHoursForProjects(conflictingProjects)
      .then(result => {
        if (!cancelled) setRoleMonthlyHours(result);
      })
      .catch(() => {
        // Availability falls back to an even spread when monthly data is unavailable
        if (!cancelled) setRoleMonthlyHours(new Map());
      });

    return () => {
      cancelled = true;
    };
    // conflictingProjects is rebuilt on every allocation change; the project ids are what matter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsKey]);

  return roleMonthlyHours;
}
